import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../../common/CCUtils"
import { log } from "../../../../common/Logging"
import { FontType } from "../../../../common/TextUtils"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { Buttons } from "../../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../../generic-sprites/ExpandoSprite"
import {
    GenericAssets,
    loadGenericAssets,
} from "../../../generic-sprites/GenericAssets"
import { Audio, AudioContainer } from "../../../utils/Audio"
import { CameraZoom } from "../../../utils/CameraZoom"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import {
    TobaccoTourProgressStore,
    TobaccoTourStages,
} from "../TobaccoTourProgressStore"
import { EcigTourAssets, loadAssets } from "./EcigTourAssets"
import { EcigTourPos } from "./EcigTourPos"
import { EcigTourKeys, EcigTourRepo } from "./EcigTourRepo"

export class EcigTourState extends Phaser.State {
    private moleculesGroup: Phaser.Group
    private hiddenMoleculesGroup: Phaser.Group
    private scenesGroup: Phaser.Group
    private eliquidBackground: Phaser.Image

    private acetaldehyde: ExpandoSprite
    private cadmium: ExpandoSprite
    private lead: ExpandoSprite
    private nicotine: ExpandoSprite
    private propyleneGlycol: ExpandoSprite

    private hiddenAcetaldehyde: Phaser.Sprite
    private hiddenCadmium: Phaser.Sprite
    private hiddenLead: Phaser.Sprite
    private hiddenNicotine: Phaser.Sprite
    private hiddenPropyleneGlycol: Phaser.Sprite

    private letsReviewButton: Phaser.Group

    private preMoleculesAudio: AudioContainer

    private skipButton: Phaser.Button

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorGray(this.game)

        this.eliquidBackground = this.game.add.image(
            0,
            0,
            EcigTourAssets.ELIQUID_BACKGROUND,
        )
        Scale.fillScreenHeight(this.eliquidBackground)

        this.scenesGroup = this.game.add.group()
        this.moleculesGroup = this.game.add.group()
        this.hiddenMoleculesGroup = this.game.add.group()

        this.createMainScene()
        this.moleculesGroup.visible = false

        this.skipButton = this.game.add.button(
            EcigTourPos.skipButtonX(),
            EcigTourPos.skipButtonY(),
            GenericAssets.NEXT_BUTTON,
            null,
            this,
            1,
            0,
        )
        this.skipButton.visible = CCUtils.debug
        Scale.scaleSprite(this.skipButton, 0.9)

        this.introScene()
        CCUtils.clearRepoKeys(EcigTourKeys)
    }

    introScene = () => {
        const introScene = this.game.add.image(0, 0, EcigTourAssets.INTRO_SCENE)
        Scale.fillScreenHeight(introScene)

        const audio = Audio.add(this.game, EcigTourAssets.INTRO_AUDIO, {
            onStop: () => {
                Buttons.makeSpriteButton(introScene)
                introScene.events.onInputDown.add(moveOn)
            },
        })

        const moveOn = () => {
            audio.audio.stop()
            introScene.destroy()
            this.viscousLiquidScene()
        }

        this.skipButton.onInputDown.addOnce(moveOn)
        this.skipButton.bringToTop()
    }

    viscousLiquidScene = () => {
        const viscousLiquid = this.game.add.image(
            0,
            0,
            EcigTourAssets.ELIQUID_MOLECULES,
        )
        Scale.scaleSprite(viscousLiquid, 0.3)
        Sprites.centerInWorld(viscousLiquid)
        const undoZoom = CameraZoom.zoom(this.game, viscousLiquid, 2000)

        const zoomOver = this.game.time.create()
        zoomOver.add(CCUtils.debug ? 2100 : 5000, () => {
            viscousLiquid.destroy()
            CameraZoom.instaZoom(undoZoom)
            this.toggleMainScene()
            if (CCUtils.debug) {
                // For debugging kill this audio after two seconds to not waste time.
                this.preMoleculesAudio.audio.stop()
            }
        })

        this.preMoleculesAudio = Audio.add(
            this.game,
            EcigTourAssets.CLICK_MOLECULE,
            {
                onPlay: () => zoomOver.start(),
                onStop: () => {
                    this.moleculesGroup.forEach((molecule: ExpandoSprite) => {
                        molecule.on = true
                    }, this)
                },
            },
        )
        this.skipButton.onInputDown.removeAll()
    }

    resetHiddenMolecules = () => {
        this.hiddenAcetaldehyde.x = this.acetaldehyde.expandoOffsetX()
        this.hiddenAcetaldehyde.y = this.acetaldehyde.expandoOffsetY()

        this.hiddenCadmium.x = this.cadmium.expandoOffsetX()
        this.hiddenCadmium.y = this.cadmium.expandoOffsetY()

        this.hiddenLead.x = this.lead.expandoOffsetX()
        this.hiddenLead.y = this.lead.expandoOffsetY()

        this.hiddenNicotine.x = this.nicotine.expandoOffsetX()
        this.hiddenNicotine.y = this.nicotine.expandoOffsetY()

        this.hiddenPropyleneGlycol.x = this.propyleneGlycol.expandoOffsetX()
        this.hiddenPropyleneGlycol.y = this.propyleneGlycol.expandoOffsetY()

        this.hiddenMoleculesGroup.forEach((hiddenMolecule) => {
            hiddenMolecule.visible = false
        }, this)
    }

    createMainScene = () => {
        const moleculeScale = 0.5
        const hiddenMoleculeScale = moleculeScale * ExpandoSprite.SCALE_AMOUNT
        const moleculesOn = false
        const tourExpandoFactory = ExpandoSprite.expandoFactory(
            this.game,
            moleculeScale,
            moleculesOn,
            this.moleculesGroup,
        )
        this.acetaldehyde = tourExpandoFactory(
            EcigTourPos.acetalehydeX(),
            EcigTourPos.acetalehydeY(),
            EcigTourAssets.ACETALDEHYDE,
        )
        this.acetaldehyde.addTitle("ACETALDEHYDE", {
            x: this.acetaldehyde.x,
            y: this.acetaldehyde.y + this.acetaldehyde.height,
            fontType: FontType.SCIENTIFIC_TITLE,
        })
        this.hiddenAcetaldehyde = this.hiddenMoleculesGroup.create(
            this.acetaldehyde.expandoOffsetX(),
            this.acetaldehyde.expandoOffsetY(),
            EcigTourAssets.ACETALDEHYDE,
        )
        Scale.scaleSprite(this.hiddenAcetaldehyde, hiddenMoleculeScale)

        this.cadmium = tourExpandoFactory(
            EcigTourPos.cadmiumX(),
            EcigTourPos.cadmiumY(),
            EcigTourAssets.CADMIUM,
        )
        this.cadmium.addTitle("CADMIUM", {
            x: this.cadmium.x + this.cadmium.width * 0.1,
            y: this.cadmium.y + this.cadmium.height * 0.6,
            fontType: FontType.SCIENTIFIC_TITLE,
        })
        this.hiddenCadmium = this.hiddenMoleculesGroup.create(
            this.cadmium.expandoOffsetX(),
            this.cadmium.expandoOffsetY(),
            EcigTourAssets.CADMIUM,
        )
        Scale.scaleSprite(this.hiddenCadmium, hiddenMoleculeScale)

        this.lead = tourExpandoFactory(
            EcigTourPos.leadX(),
            EcigTourPos.leadY(),
            EcigTourAssets.LEAD,
        )
        this.lead.addTitle("LEAD", {
            x: this.lead.x,
            y: this.lead.y + this.lead.height * 0.6,
            fontType: FontType.SCIENTIFIC_TITLE,
        })
        this.hiddenLead = this.hiddenMoleculesGroup.create(
            this.lead.expandoOffsetX(),
            this.lead.expandoOffsetY(),
            EcigTourAssets.LEAD,
        )
        Scale.scaleSprite(this.hiddenLead, hiddenMoleculeScale)

        this.nicotine = tourExpandoFactory(
            EcigTourPos.nicotineX(),
            EcigTourPos.nicotineY(),
            EcigTourAssets.NICOTINE,
        )
        this.nicotine.addTitle("NICOTINE", {
            x: this.nicotine.x,
            y: this.nicotine.y + this.nicotine.height * 0.5,
            fontType: FontType.SCIENTIFIC_TITLE,
        })
        this.hiddenNicotine = this.hiddenMoleculesGroup.create(
            this.nicotine.expandoOffsetX(),
            this.nicotine.expandoOffsetY(),
            EcigTourAssets.NICOTINE,
        )
        Scale.scaleSprite(this.hiddenNicotine, hiddenMoleculeScale)

        this.propyleneGlycol = tourExpandoFactory(
            EcigTourPos.propyleneGlycolX(),
            EcigTourPos.propyleneGlycolY(),
            EcigTourAssets.PROPYLENE_GLYCOL,
        )
        this.propyleneGlycol.addTitle("PROPYLENE GLYCOL", {
            x: this.propyleneGlycol.x,
            y: this.propyleneGlycol.y + this.propyleneGlycol.height,
            fontType: FontType.SCIENTIFIC_TITLE,
        })
        this.hiddenPropyleneGlycol = this.hiddenMoleculesGroup.create(
            this.propyleneGlycol.expandoOffsetX(),
            this.propyleneGlycol.expandoOffsetY(),
            EcigTourAssets.PROPYLENE_GLYCOL,
        )
        Scale.scaleSprite(this.hiddenPropyleneGlycol, hiddenMoleculeScale)

        this.resetHiddenMolecules()

        this.moleculesGroup.forEach((molecule) => {
            Scale.scaleSprite(molecule, moleculeScale)
            molecule.inputEnabled = true

            molecule.addOnClick((clickedMolecule) => {
                if (this.letsReviewButton) {
                    this.letsReviewButton.visible = false
                }
                this.moleculesGroup.forEach((innerMolecule: ExpandoSprite) => {
                    innerMolecule.on = false
                    innerMolecule.input.enabled = false
                    innerMolecule.title.visible = false
                    if (innerMolecule === clickedMolecule) {
                        innerMolecule.visible = false
                    }
                    Sprites.fadeOut(innerMolecule)
                }, this)

                Sprites.fadeOut(this.eliquidBackground)

                let moveToX = EcigTourPos.moleculeLocation1X()
                let moveToY = EcigTourPos.moleculeLocationY()
                let move: Phaser.Sprite

                switch (molecule.key) {
                    case EcigTourAssets.ACETALDEHYDE:
                        this.acetalehydeScene()
                        move = this.hiddenAcetaldehyde
                        break
                    case EcigTourAssets.CADMIUM:
                        this.cadmiumScene()
                        move = this.hiddenCadmium
                        break
                    case EcigTourAssets.LEAD:
                        this.leadScene()
                        move = this.hiddenLead
                        break
                    case EcigTourAssets.NICOTINE:
                        this.nicotineScene()
                        move = this.hiddenNicotine
                        moveToX = EcigTourPos.nicotineX2()
                        moveToY = EcigTourPos.nicotineY2()
                        break
                    case EcigTourAssets.PROPYLENE_GLYCOL:
                        this.propyleneGlycolScene()
                        move = this.hiddenPropyleneGlycol
                        moveToX = EcigTourPos.propyleneGlycolX2()
                        moveToY = EcigTourPos.propyleneGlycolY2()
                        break
                    default:
                        throw new Error("Unexpected tour asset for molecule.")
                }

                move.visible = true
                CameraZoom.moveSprite(move, moveToX, moveToY)
            })
        }, this)
    }

    toggleMainScene = () => {
        this.moleculesGroup.visible = true

        this.eliquidBackground.alpha = 1
        this.eliquidBackground.visible = true

        let allMoleculesComplete = true
        this.moleculesGroup.forEach((molecule: ExpandoSprite) => {
            log.debug(
                "is pre molecules playing? " +
                    this.preMoleculesAudio.audio.isPlaying +
                    " is turning on: " +
                    true,
            )
            if (!this.preMoleculesAudio.audio.isPlaying) {
                molecule.on = true
                if (molecule.input) {
                    molecule.input.enabled = true
                }
            }
            molecule.alpha = 1
            molecule.visible = true

            let complete: boolean
            switch (molecule.key) {
                case EcigTourAssets.ACETALDEHYDE:
                    complete = EcigTourRepo.instance.isAcetaldehydeComplete()
                    break
                case EcigTourAssets.CADMIUM:
                    complete = EcigTourRepo.instance.isCadmiumComplete()
                    break
                case EcigTourAssets.LEAD:
                    complete = EcigTourRepo.instance.isLeadComplete()
                    break
                case EcigTourAssets.NICOTINE:
                    complete = EcigTourRepo.instance.isNicotineComplete()
                    break
                case EcigTourAssets.PROPYLENE_GLYCOL:
                    complete = EcigTourRepo.instance.isPropyleneGlycolComplete()
                    break
                default:
                    throw new Error(
                        "Unexpected tour asset for molecule in main scene.",
                    )
            }
            if (complete) {
                molecule.grayOut()
            } else {
                allMoleculesComplete = false
            }
        }, this)

        if (allMoleculesComplete) {
            TobaccoTourProgressStore.instance.addStage(TobaccoTourStages.ECIG)
            this.game.state.start(ActivityKey.TOBACCO_TOUR)
        }

        // this.skipButton.onInputDown.addOnce(this.letsReview)
    }

    addScene = (
        background: string,
        audio: string,
        doctorsNote: string,
        noteTiming: number,
        ecigStage: EcigTourKeys,
        noteX = EcigTourPos.bottomLeftNoteX(),
        noteY = EcigTourPos.bottomLeftNoteY(),
    ) => {
        const scene = this.scenesGroup.create(0, 0, background)
        Scale.fillScreenHeight(scene)
        scene.alpha = 0
        Sprites.fade(scene, {
            fadeDuration: 1000,
            onComplete: () => {
                const showNote = this.game.time.create()
                let note: Phaser.Image
                Audio.add(this.game, audio, {
                    onPlay: () => showNote.start(),
                    onStop: () => {
                        const turnOffScene = this.game.time.create()
                        turnOffScene.add(1000, () => {
                            scene.destroy()
                            note.destroy()
                            this.resetHiddenMolecules()
                            EcigTourRepo.instance.setStageComplete(ecigStage)
                            this.toggleMainScene()
                        })
                        turnOffScene.start()
                    },
                })
                showNote.add(noteTiming, () => {
                    note = this.game.add.image(noteX, noteY, doctorsNote)
                    Scale.scaleSprite(note, 0.5)
                    note.alpha = 0

                    Sprites.fade(note)
                })
            },
        })
    }

    acetalehydeScene = () => {
        this.addScene(
            EcigTourAssets.ACETALEHYDE_SCENE,
            EcigTourAssets.ACETALDEHYDE_AUDIO,
            EcigTourAssets.ACETALEHYDE_NOTE,
            6000,
            EcigTourKeys.ACETALDEHYDE_COMPLETE,
        )
    }

    cadmiumScene = () => {
        this.addScene(
            EcigTourAssets.CADMIUM_SCENE,
            EcigTourAssets.CADMIUM_AUDIO,
            EcigTourAssets.CADMIUM_NOTE,
            4000,
            EcigTourKeys.CADMIUM_COMPLETE,
        )
    }

    leadScene = () => {
        this.addScene(
            EcigTourAssets.LEAD_SCENE,
            EcigTourAssets.LEAD_AUDIO,
            EcigTourAssets.LEAD_NOTE,
            6000,
            EcigTourKeys.LEAD_COMPLETE,
        )
    }

    nicotineScene = () => {
        this.addScene(
            EcigTourAssets.NICOTINE_SCENE,
            EcigTourAssets.NICOTINE_AUDIO,
            EcigTourAssets.NICOTINE_NOTE,
            10000,
            EcigTourKeys.NICOTINE_COMPLETE,
            EcigTourPos.nicotineNoteX(),
            EcigTourPos.nicotineNoteY(),
        )
    }

    propyleneGlycolScene = () => {
        this.addScene(
            EcigTourAssets.PROPYLENE_GLYCOL_SCENE,
            EcigTourAssets.PROPYLENE_GLYCOL_AUDIO,
            EcigTourAssets.PROPYLENE_GLYCOL_NOTE,
            12000,
            EcigTourKeys.PROPYLENE_GLYCOL_COMPLETE,
        )
    }
}
